"use client";

import React, { useEffect, useState, useRef } from "react";
import { db } from "@/firebase/firebase";
import { doc, DocumentData, getDoc } from "firebase/firestore";
import dynamic from "next/dynamic";
import LoadingSpinner from "../Reuseable/Loading";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useSpring, useTransform, AnimatePresence } from "motion/react";
import { Button } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  Calendar01Icon,
  User02Icon,
  Clock01Icon,
  Copy01Icon,
  Tick01Icon,
  Menu01Icon,
} from "@hugeicons/core-free-icons";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/Tooltip";

const ReadOnlyEditor = dynamic(
  () => import("@/components/Reuseable/Editor/ReadOnlyEditor"),
  {
    ssr: false,
    loading: () => <LoadingSpinner />,
  },
);

const MotionLink = motion(Link);

// Dynamically determine category if not defined in database (matches BlogPage/BlogPostCard)
function getCategory(post: DocumentData) {
  if (post?.category) return post.category;
  const titleLower = (post?.title || "").toLowerCase();

  if (
    titleLower.includes("devops") ||
    titleLower.includes("cloud") ||
    titleLower.includes("aws") ||
    titleLower.includes("docker") ||
    titleLower.includes("kubernetes") ||
    titleLower.includes("ci/cd") ||
    titleLower.includes("deployment")
  ) {
    return "DevOps & Cloud";
  }
  if (
    titleLower.includes("ai") ||
    titleLower.includes("ml") ||
    titleLower.includes("artificial intelligence") ||
    titleLower.includes("machine learning") ||
    titleLower.includes("llm") ||
    titleLower.includes("openai") ||
    titleLower.includes("gpt")
  ) {
    return "AI & ML";
  }
  if (
    titleLower.includes("database") ||
    titleLower.includes("sql") ||
    titleLower.includes("postgres") ||
    titleLower.includes("mongodb") ||
    titleLower.includes("nosql") ||
    titleLower.includes("redis") ||
    titleLower.includes("firestore") ||
    titleLower.includes("firebase")
  ) {
    return "Database";
  }
  if (
    titleLower.includes("frontend") ||
    titleLower.includes("tailwind") ||
    titleLower.includes("javascript") ||
    titleLower.includes("typescript") ||
    titleLower.includes("react") ||
    titleLower.includes("next")
  ) {
    return "Frontend";
  }
  if (
    titleLower.includes("backend") ||
    titleLower.includes("api") ||
    titleLower.includes("node") ||
    titleLower.includes("express") ||
    titleLower.includes("server") ||
    titleLower.includes("rust") ||
    titleLower.includes("go")
  ) {
    return "Backend";
  }
  if (
    titleLower.includes("web") ||
    titleLower.includes("website") ||
    titleLower.includes("html") ||
    titleLower.includes("css") ||
    titleLower.includes("fullstack")
  ) {
    return "Web Development";
  }
  if (
    titleLower.includes("mobile") ||
    titleLower.includes("app") ||
    titleLower.includes("ios") ||
    titleLower.includes("android") ||
    titleLower.includes("flutter") ||
    titleLower.includes("react native") ||
    titleLower.includes("swift")
  ) {
    return "Mobile Development";
  }
  if (
    titleLower.includes("software") ||
    titleLower.includes("system") ||
    titleLower.includes("architecture") ||
    titleLower.includes("pattern") ||
    titleLower.includes("algorithm")
  ) {
    return "Software Development";
  }
  if (
    titleLower.includes("design") ||
    titleLower.includes("ui") ||
    titleLower.includes("ux") ||
    titleLower.includes("figma")
  ) {
    return "Design";
  }
  if (
    titleLower.includes("tutorial") ||
    titleLower.includes("guide") ||
    titleLower.includes("how to") ||
    titleLower.includes("build")
  ) {
    return "Tutorial";
  }
  if (
    titleLower.includes("engineering") ||
    titleLower.includes("code") ||
    titleLower.includes("tech")
  ) {
    return "Engineering";
  }
  if (
    titleLower.includes("news") ||
    titleLower.includes("announcing") ||
    titleLower.includes("update") ||
    titleLower.includes("launch")
  ) {
    return "News";
  }
  return "Insight";
}

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

const categoryConfig: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  "Web Development": {
    bg: "bg-cyan-500/10 dark:bg-cyan-500/5",
    text: "text-cyan-600 dark:text-cyan-400",
    border: "border-cyan-500/20 dark:border-cyan-500/10",
    dot: "bg-cyan-500"
  },
  "Mobile Development": {
    bg: "bg-indigo-500/10 dark:bg-indigo-500/5",
    text: "text-indigo-600 dark:text-indigo-400",
    border: "border-indigo-500/20 dark:border-indigo-500/10",
    dot: "bg-indigo-500"
  },
  "Software Development": {
    bg: "bg-teal-500/10 dark:bg-teal-500/5",
    text: "text-teal-600 dark:text-teal-400",
    border: "border-teal-500/20 dark:border-teal-500/10",
    dot: "bg-teal-500"
  },
  Frontend: {
    bg: "bg-sky-500/10 dark:bg-sky-500/5",
    text: "text-sky-600 dark:text-sky-400",
    border: "border-sky-500/20 dark:border-sky-500/10",
    dot: "bg-sky-500"
  },
  Backend: {
    bg: "bg-blue-500/10 dark:bg-blue-500/5",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-500/20 dark:border-blue-500/10",
    dot: "bg-blue-500"
  },
  "DevOps & Cloud": {
    bg: "bg-purple-500/10 dark:bg-purple-500/5",
    text: "text-purple-600 dark:text-purple-400",
    border: "border-purple-500/20 dark:border-purple-500/10",
    dot: "bg-purple-500"
  },
  "AI & ML": {
    bg: "bg-rose-500/10 dark:bg-rose-500/5",
    text: "text-rose-600 dark:text-rose-400",
    border: "border-rose-500/20 dark:border-rose-500/10",
    dot: "bg-rose-500"
  },
  Database: {
    bg: "bg-amber-500/10 dark:bg-amber-500/5",
    text: "text-amber-600 dark:text-amber-400",
    border: "border-amber-500/20 dark:border-amber-500/10",
    dot: "bg-amber-500"
  },
  Design: {
    bg: "bg-pink-500/10 dark:bg-pink-500/5",
    text: "text-pink-600 dark:text-pink-400",
    border: "border-pink-500/20 dark:border-pink-500/10",
    dot: "bg-pink-500"
  },
  Engineering: {
    bg: "bg-blue-500/10 dark:bg-blue-500/5",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-500/20 dark:border-blue-500/10",
    dot: "bg-blue-500"
  },
  Tutorial: {
    bg: "bg-emerald-500/10 dark:bg-emerald-500/5",
    text: "text-emerald-600 dark:text-emerald-400",
    border: "border-emerald-500/20 dark:border-emerald-500/10",
    dot: "bg-emerald-500"
  },
  News: {
    bg: "bg-purple-500/10 dark:bg-purple-500/5",
    text: "text-purple-600 dark:text-purple-400",
    border: "border-purple-500/20 dark:border-purple-500/10",
    dot: "bg-purple-500"
  },
  Insight: {
    bg: "bg-amber-500/10 dark:bg-amber-500/5",
    text: "text-amber-600 dark:text-amber-400",
    border: "border-amber-500/20 dark:border-amber-500/10",
    dot: "bg-amber-500"
  }
};

export default function BlogContent({ blogId }: { blogId: string }) {
  const [blogData, setBlogData] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);
  const [scrollPercentage, setScrollPercentage] = useState<number>(0);
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [tocOpen, setTocOpen] = useState<boolean>(true);
  const [shareOpen, setShareOpen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollY, scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const posterY = useTransform(scrollY, [0, 800], [0, 100]);
  const posterScale = useTransform(scrollY, [0, 800], [1, 1.04]);
  const posterOpacity = useTransform(scrollY, [0, 800], [1, 0.85]);

  useEffect(() => {
    if (blogData?.content) {
      // Parse headings directly from the HTML string to generate the list of headings
      const parser = new DOMParser();
      const doc = parser.parseFromString(blogData.content, "text/html");
      const headingElements = doc.querySelectorAll("h1, h2, h3");
      const items: HeadingItem[] = [];

      headingElements.forEach((el, index) => {
        const text = el.textContent || "";
        const level = parseInt(el.tagName.replace("H", ""), 10);
        const id = el.id || text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || `heading-${index}`;
        items.push({ id, text, level });
      });

      setHeadings(items);
    }
  }, [blogData]);

  useEffect(() => {
    if (headings.length === 0) return;

    // Synchronize IDs with the actual rendered DOM elements
    const syncDOM = () => {
      const articleEl = document.querySelector("article");
      if (!articleEl) return false;

      const proseEl = articleEl.querySelector(".prose");
      if (!proseEl) return false;

      const headingElements = proseEl.querySelectorAll("h1, h2, h3");
      if (headingElements.length === 0) return false;

      headingElements.forEach((el, index) => {
        const text = el.textContent || "";
        const id = el.id || text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || `heading-${index}`;
        el.id = id;
      });
      return true;
    };

    // Try immediately
    if (syncDOM()) return;

    // If not loaded yet, poll periodically for 3 seconds
    let count = 0;
    const interval = setInterval(() => {
      count++;
      if (syncDOM() || count >= 15) {
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [headings]);

  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          const closest = visibleEntries.reduce((prev, curr) => {
            return Math.abs(curr.boundingClientRect.top) < Math.abs(prev.boundingClientRect.top) ? curr : prev;
          });
          setActiveId(closest.target.id);
        }
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0.1,
      }
    );

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });

    return () => {
      headings.forEach((heading) => {
        const el = document.getElementById(heading.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [headings]);

  useEffect(() => {
    getBlogData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight =
        document.documentElement.scrollHeight - windowHeight;
      if (documentHeight > 0) {
        const scrolled = (window.scrollY / documentHeight) * 100;
        setScrollPercentage(scrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getBlogData = async () => {
    try {
      const ref = `blog/${blogId}`;
      const docRef = doc(db, ref);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setBlogData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToTwitter = () => {
    if (typeof window !== "undefined" && blogData) {
      const text = `Read "${blogData.title}" by ${blogData.author || "Anonymous"} on devebsy`;
      const url = window.location.href;
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        "_blank",
      );
    }
  };

  const shareToLinkedIn = () => {
    if (typeof window !== "undefined") {
      const url = window.location.href;
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        "_blank",
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50/10 dark:bg-zinc-950/10 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute top-[20%] left-[20%] w-[30%] h-[30%] rounded-full bg-blue-500/10 dark:bg-blue-500/5 blur-[100px]" />
          <div className="absolute bottom-[20%] right-[20%] w-[35%] h-[35%] rounded-full bg-purple-500/10 dark:bg-purple-500/5 blur-[100px]" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-3">
          <LoadingSpinner />
          <span className="text-xs font-bold text-neutral-400 dark:text-zinc-500 uppercase tracking-widest font-outfit animate-pulse">
            Fetching article...
          </span>
        </div>
      </div>
    );
  }

  if (!blogData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50/10 dark:bg-zinc-950/10 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute top-[20%] left-[20%] w-[30%] h-[30%] rounded-full bg-red-500/5 dark:bg-red-500/2 blur-[100px]" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center gap-5 max-w-sm px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 dark:bg-red-500/20 border border-red-500/20 flex items-center justify-center text-red-500 text-2xl font-bold font-outfit">
            !
          </div>
          <div>
            <h2 className="text-xl font-bold font-recoleta text-neutral-850 dark:text-neutral-200">
              Article Not Found
            </h2>
            <p className="text-xs text-neutral-500 dark:text-zinc-500 font-outfit mt-1 leading-relaxed">
              We couldn't retrieve the article details. It might have been
              moved, deleted, or the address is incorrect.
            </p>
          </div>
            <Button
              render={(props) => <Link {...(props as any)} href="/blog" />}
              className="rounded-full font-bold font-outfit bg-neutral-900 hover:bg-neutral-800 dark:bg-white dark:hover:bg-neutral-100 text-white dark:text-black shadow-md hover:scale-102 active:scale-98 transition-all px-6"
              size="sm"
            >
              Back to Journal
            </Button>
        </div>
      </div>
    );
  }

  const formattedDate = blogData.createdAt
    ? typeof blogData.createdAt.toDate === "function"
      ? blogData.createdAt.toDate().toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : new Date(blogData.createdAt).toLocaleDateString()
    : "";

  const category = getCategory(blogData);
  const catStyle = categoryConfig[category] || categoryConfig.Insight;

  // Live calculated reading time based on content text word count
  const articleStats = (() => {
    if (!blogData.content) {
      return { words: 0, readingTime: "2 min read" };
    }
    const plainText = blogData.content
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ");
    const words = plainText.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 225)); // 225 words per min average
    return {
      words,
      readingTime: `${minutes} min read`,
    };
  })();

  // Generates initials for the author avatar
  const authorInitials = (() => {
    const authorName = blogData.author || "Anonymous";
    const parts = authorName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  })();

  return (
    <div ref={containerRef} className="min-h-screen pb-32 relative bg-neutral-50/20 dark:bg-[#00000b] text-neutral-850 dark:text-zinc-150 overflow-x-clip transition-colors duration-300">
      {/* Scroll Progress Indicator at top */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 origin-left z-50 shadow-[0_1px_10px_rgba(59,130,246,0.5)]"
        style={{ scaleX }}
      />

      {/* Ambient Poster Blur Background (Apple Music Style) */}
      {blogData.poster && (
        <div className="absolute top-0 inset-x-0 h-[700px] overflow-hidden pointer-events-none z-0 select-none opacity-20 dark:opacity-30">
          <div
            className="absolute inset-0 bg-cover bg-center filter blur-[100px] scale-150 transform translate-y-[-15%]"
            style={{ backgroundImage: `url(${blogData.poster})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-50/60 dark:via-[#00000b]/60 to-neutral-50 dark:to-[#00000b]" />
        </div>
      )}

      {/* Ambient Grid Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-500/5 dark:bg-indigo-500/2 blur-[120px]" />
        <div className="absolute top-[30%] -right-[10%] w-[60%] h-[60%] rounded-full bg-cyan-500/5 dark:bg-cyan-500/2 blur-[140px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808005_1px,transparent_1px),linear-gradient(to_bottom,#80808005_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>

      {/* Top Header Row */}
      <div className="max-w-6xl mx-auto px-4 pt-8 pb-4 relative z-10 flex items-center justify-between">
          <Button
            render={(props) => <Link {...(props as any)} href="/blog" />}
            variant="ghost"
            className="hover:bg-neutral-200/50 dark:hover:bg-zinc-900/50 group text-neutral-600 dark:text-zinc-400 font-semibold font-outfit rounded-full text-xs"
            size="sm"
          >
            <HugeiconsIcon
              icon={ArrowLeft01Icon}
              className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1"
              size={14}
            />
            Journal
          </Button>
        <span className="text-[11px] font-bold text-neutral-400 dark:text-zinc-500 uppercase tracking-widest font-outfit">
          Devebsy Publication
        </span>
      </div>

      {/* Main Content Area */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 z-10">
        
        {/* Desktop Sticky Left Console - Docked vertically */}
        <div className="hidden lg:block absolute -left-16 xl:-left-24 top-[350px] h-[calc(100%-480px)] w-14 z-20">
          <div className="sticky top-32 flex flex-col gap-6 items-center">
            
            {/* Unified Vertical Control Console */}
            <div className="flex flex-col gap-5 p-2.5 rounded-full border border-neutral-250/50 dark:border-zinc-800/40 bg-white/70 dark:bg-zinc-950/60 backdrop-blur-2xl shadow-[0_20px_50px_rgba(0,0,0,0.06)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] transition-all duration-300 hover:border-indigo-500/20 dark:hover:border-indigo-500/20">
              
              {/* Back to blog */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <MotionLink
                    href="/blog"
                    whileHover={{ scale: 1.08, y: -2 }}
                    whileTap={{ scale: 0.92 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                    className="p-2.5 rounded-full hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 hover:text-neutral-900 dark:text-zinc-400 dark:hover:text-white transition-colors cursor-pointer outline-none flex items-center justify-center"
                  >
                    <HugeiconsIcon
                      icon={ArrowLeft01Icon}
                      className="w-4 h-4"
                      size={16}
                    />
                  </MotionLink>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12} className="font-outfit font-medium text-xs bg-neutral-900 text-white dark:bg-white dark:text-black px-2 py-1 shadow-md">
                  Back to Journal
                </TooltipContent>
              </Tooltip>

              <div className="w-6 h-[1px] bg-neutral-200/80 dark:bg-zinc-800/60 mx-auto" />

              {/* Collapsible Outline Toggle */}
              {headings.length > 0 && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.button
                        onClick={() => setTocOpen(!tocOpen)}
                        whileHover={{ scale: 1.08, y: -2 }}
                        whileTap={{ scale: 0.92 }}
                        transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        className={`p-2.5 rounded-full transition-all cursor-pointer outline-none flex items-center justify-center ${
                          tocOpen 
                            ? "bg-indigo-500/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400 border border-indigo-500/20" 
                            : "hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400"
                        }`}
                      >
                        <HugeiconsIcon
                          icon={Menu01Icon}
                          className="w-4 h-4"
                          size={16}
                        />
                      </motion.button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={12} className="font-outfit font-medium text-xs bg-neutral-900 text-white dark:bg-white dark:text-black px-2 py-1 shadow-md">
                      {tocOpen ? "Hide Outline" : "Show Outline"}
                    </TooltipContent>
                  </Tooltip>
                  <div className="w-6 h-[1px] bg-neutral-200/80 dark:bg-zinc-800/60 mx-auto" />
                </>
              )}

              {/* Share Action Trigger */}
              <div className="relative flex flex-col items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={() => setShareOpen(!shareOpen)}
                      whileHover={{ scale: 1.08, y: -2 }}
                      whileTap={{ scale: 0.92 }}
                      transition={{ type: "spring", stiffness: 400, damping: 15 }}
                      className={`p-2.5 rounded-full transition-all cursor-pointer outline-none flex items-center justify-center ${
                        shareOpen 
                          ? "bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 border border-blue-500/20" 
                          : "hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-450"
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 10.742l4.636-2.318m0 7.152l-4.636-2.318M21 12a3 3 0 11-6 0 3 3 0 016 0zm-11-7a3 3 0 11-6 0 3 3 0 016 0zm0 14a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12} className="font-outfit font-medium text-xs bg-neutral-900 text-white dark:bg-white dark:text-black px-2 py-1 shadow-md">
                    Share publication
                  </TooltipContent>
                </Tooltip>

                {/* Staggered flying share menu */}
                <AnimatePresence>
                  {shareOpen && (
                    <motion.div
                      initial={{ opacity: 0, x: 20, scale: 0.9 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: 20, scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 420, damping: 22 }}
                      className="absolute left-16 top-0 bg-white/95 dark:bg-zinc-950/95 border border-neutral-250/50 dark:border-zinc-850 p-2.5 rounded-2xl shadow-xl flex flex-col gap-2.5 z-50 min-w-[145px] backdrop-blur-xl"
                    >
                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          handleCopyLink();
                          setShareOpen(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold font-outfit rounded-lg hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-600 dark:text-zinc-400 hover:text-neutral-900 dark:hover:text-white text-left transition-colors"
                      >
                        {copied ? (
                          <HugeiconsIcon icon={Tick01Icon} className="w-3.5 h-3.5 text-emerald-500" size={14} />
                        ) : (
                          <HugeiconsIcon icon={Copy01Icon} className="w-3.5 h-3.5" size={14} />
                        )}
                        <span>{copied ? "Copied!" : "Copy URL"}</span>
                      </button>

                      {/* Share X */}
                      <button
                        onClick={() => {
                          shareToTwitter();
                          setShareOpen(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold font-outfit rounded-lg hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-600 dark:text-zinc-400 hover:text-neutral-900 dark:hover:text-white text-left transition-colors"
                      >
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        <span>Share on X</span>
                      </button>

                      {/* Share LinkedIn */}
                      <button
                        onClick={() => {
                          shareToLinkedIn();
                          setShareOpen(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold font-outfit rounded-lg hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-600 dark:text-zinc-400 hover:text-neutral-900 dark:hover:text-white text-left transition-colors"
                      >
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                        </svg>
                        <span>LinkedIn</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Glowing vertical reading progress pill */}
            <div className="relative w-12 h-12 flex items-center justify-center select-none group">
              <div className="absolute inset-0 bg-indigo-500/5 dark:bg-indigo-400/5 rounded-full blur-sm transition-all duration-300 group-hover:bg-indigo-500/10" />
              <svg className="w-12 h-12 transform -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  className="stroke-neutral-200/50 dark:stroke-zinc-800/80"
                  strokeWidth="3.5"
                  fill="transparent"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  className="stroke-indigo-500 dark:stroke-indigo-400 transition-all duration-150"
                  strokeWidth="3.5"
                  fill="transparent"
                  strokeDasharray={2 * Math.PI * 20}
                  strokeDashoffset={2 * Math.PI * 20 * (1 - scrollPercentage / 100)}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center font-outfit">
                <span className="text-[10px] font-bold text-neutral-800 dark:text-zinc-200 leading-none">
                  {Math.round(scrollPercentage)}%
                </span>
                <span className="text-[6px] text-neutral-400 dark:text-zinc-500 uppercase tracking-widest mt-0.5 scale-90">
                  Read
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Sticky HUD Table of Contents - Right side */}
        {headings.length > 0 && (
          <AnimatePresence>
            {tocOpen && (
              <motion.div
                initial={{ opacity: 0, x: 40, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 350, damping: 26 }}
                className="hidden xl:block absolute -right-68 top-[350px] h-[calc(100%-480px)] w-60 z-20"
              >
                <div className="sticky top-32 p-5 rounded-[24px] border border-neutral-250/50 dark:border-zinc-800/40 bg-white/70 dark:bg-zinc-950/50 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.06)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] transition-all duration-300 hover:border-indigo-500/20 dark:hover:border-indigo-500/20">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] font-bold text-neutral-400 dark:text-zinc-500 uppercase tracking-widest font-outfit">
                      Outline
                    </p>
                    <button
                      onClick={() => setTocOpen(false)}
                      className="text-neutral-400 hover:text-neutral-900 dark:text-zinc-500 dark:hover:text-zinc-200 transition-colors p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-zinc-900"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <nav className="flex flex-col space-y-1.5 text-xs font-outfit relative pl-3 border-l border-neutral-200 dark:border-zinc-850">
                    {headings.map((heading) => (
                      <button
                        key={heading.id}
                        onClick={() => {
                          const el = document.getElementById(heading.id);
                          if (el) {
                            const elementPosition = el.getBoundingClientRect().top + window.scrollY;
                            const offsetPosition = elementPosition - 100;
                            window.scrollTo({
                              top: offsetPosition,
                              behavior: "smooth"
                            });
                          }
                        }}
                        className={`group relative text-left transition-all duration-200 hover:text-indigo-600 dark:hover:text-indigo-400 line-clamp-2 py-1.5 cursor-pointer outline-none ${
                          heading.level === 1
                            ? "font-bold text-[13px]"
                            : heading.level === 2
                            ? "pl-3 text-neutral-500 dark:text-zinc-400"
                            : "pl-6 text-neutral-400 dark:text-zinc-500"
                        } ${
                          activeId === heading.id
                            ? "text-indigo-600 dark:text-indigo-400 font-extrabold"
                            : "text-neutral-600 dark:text-zinc-400"
                        }`}
                      >
                        {activeId === heading.id && (
                          <motion.span
                            layoutId="active-heading-marker"
                            className="absolute left-[-15px] top-1.5 bottom-1.5 w-[3px] rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.8)]"
                            transition={{ type: "spring", stiffness: 380, damping: 28 }}
                          />
                        )}
                        {heading.text}
                      </button>
                    ))}
                  </nav>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Mobile Floating Glassmorphic Dock - Dynamic Island Style */}
        <div className="lg:hidden fixed bottom-6 inset-x-4 max-w-md mx-auto z-45 flex justify-center">
          <div className="flex items-center gap-5 px-5 py-3 rounded-full border border-neutral-200/60 dark:border-zinc-850/60 bg-white/85 dark:bg-zinc-950/85 backdrop-blur-xl shadow-[0_24px_50px_-10px_rgba(0,0,0,0.15)] dark:shadow-[0_24px_50px_-10px_rgba(0,0,0,0.5)]">
            <Link 
              href="/blog"
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 dark:text-zinc-400 transition-colors flex items-center justify-center"
            >
              <HugeiconsIcon
                icon={ArrowLeft01Icon}
                className="w-4 h-4"
                size={16}
              />
            </Link>
            <div className="w-[1px] h-5 bg-neutral-200 dark:bg-zinc-800" />
            
            {/* Outline drawer toggle for mobile */}
            {headings.length > 0 && (
              <button
                onClick={() => {
                  setTocOpen(!tocOpen);
                  if (!tocOpen) {
                    // Smooth scroll to top outline panel or handle drawer logic
                  }
                }}
                className={`p-2 rounded-full transition-all ${
                  tocOpen ? "bg-indigo-500/10 text-indigo-500" : "text-neutral-500 dark:text-zinc-400 hover:bg-neutral-100 dark:hover:bg-zinc-900"
                }`}
              >
                <HugeiconsIcon icon={Menu01Icon} className="w-4 h-4" size={16} />
              </button>
            )}

            <button
              onClick={handleCopyLink}
              className={`p-2 rounded-full transition-all ${copied ? "bg-emerald-500/10 text-emerald-500" : "hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 dark:text-zinc-400"}`}
            >
              {copied ? (
                <HugeiconsIcon
                  icon={Tick01Icon}
                  className="w-4 h-4"
                  size={16}
                />
              ) : (
                <HugeiconsIcon
                  icon={Copy01Icon}
                  className="w-4 h-4"
                  size={16}
                />
              )}
            </button>
            
            <button
              onClick={shareToTwitter}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 dark:text-zinc-400 transition-colors"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </button>
            
            <button
              onClick={shareToLinkedIn}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-zinc-900 text-neutral-500 dark:text-zinc-400 transition-colors"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
              </svg>
            </button>
            
            <div className="w-[1px] h-5 bg-neutral-200 dark:bg-zinc-800" />
            <span className="text-[10px] font-bold font-outfit text-neutral-600 dark:text-zinc-400 select-none">
              {articleStats.readingTime}
            </span>
          </div>
        </div>

        {/* Mobile Inline TOC Sheet/List - shown above content when toggled on mobile */}
        {headings.length > 0 && tocOpen && (
          <div className="lg:hidden mb-8 p-6 rounded-[24px] border border-neutral-200 dark:border-zinc-850 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">Article Outline</span>
              <button
                onClick={() => setTocOpen(false)}
                className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400"
              >
                Hide
              </button>
            </div>
            <nav className="flex flex-col space-y-2.5 text-xs font-outfit pl-2.5 border-l-2 border-indigo-500/30">
              {headings.map((heading) => (
                <button
                  key={heading.id}
                  onClick={() => {
                    const el = document.getElementById(heading.id);
                    if (el) {
                      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
                      const offsetPosition = elementPosition - 100;
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                      });
                      setTocOpen(false); // auto-close outline on click in mobile
                    }
                  }}
                  className={`text-left line-clamp-1 py-0.5 ${
                    heading.level === 1 ? "font-bold" : heading.level === 2 ? "pl-3 text-neutral-500 dark:text-zinc-400" : "pl-6 text-neutral-400"
                  } ${activeId === heading.id ? "text-indigo-600 dark:text-indigo-400 font-extrabold" : "text-neutral-600 dark:text-zinc-400"}`}
                >
                  {heading.text}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Core Article Layout */}
        <article className="max-w-3xl mx-auto">
          
          {/* Layered Cinematic Header */}
          <header className="mb-10 text-left space-y-6">
            
            {/* Category Tag & Reading Stats */}
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 px-3.5 py-1 text-[11px] font-extrabold rounded-full border backdrop-blur-md shadow-sm uppercase tracking-wider font-outfit ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${catStyle.dot} animate-pulse`} />
                {category}
              </span>
              
              <span className="text-[11px] font-bold text-neutral-500 dark:text-zinc-400 font-outfit flex items-center gap-1.5 bg-neutral-200/30 dark:bg-zinc-900/30 px-3 py-1 rounded-full border border-neutral-200/20 dark:border-zinc-800/20">
                <HugeiconsIcon
                  icon={Clock01Icon}
                  className="w-3.5 h-3.5"
                  size={14}
                />
                {articleStats.readingTime} ({articleStats.words} words)
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-recoleta font-black tracking-tight text-neutral-900 dark:text-white leading-[1.12] transition-all bg-gradient-to-r from-neutral-900 via-neutral-850 to-neutral-900 dark:from-white dark:via-zinc-100 dark:to-zinc-300 bg-clip-text text-transparent">
              {blogData.title}
            </h1>

            {/* Author Profile Info Bar */}
            <div className="flex items-center gap-4 pt-5 border-t border-neutral-200/50 dark:border-zinc-850/40">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500/10 via-indigo-500/10 to-purple-500/10 dark:from-blue-500/20 dark:via-indigo-500/20 dark:to-purple-500/20 flex items-center justify-center text-sm font-bold text-indigo-600 dark:text-indigo-400 font-outfit border border-indigo-500/20 shadow-md">
                {authorInitials}
              </div>
              <div className="text-left">
                <p className="text-sm font-extrabold text-neutral-800 dark:text-zinc-200 leading-tight">
                  {blogData.author || "Anonymous"}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-neutral-500 dark:text-zinc-500 font-outfit">
                  <span className="font-semibold text-neutral-400 dark:text-zinc-500">
                    Author
                  </span>
                  <span>&bull;</span>
                  <span className="flex items-center gap-1">
                    <HugeiconsIcon
                      icon={Calendar01Icon}
                      className="w-3 h-3"
                      size={12}
                    />
                    {formattedDate}
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Poster Image Frame with Parallax/Zoom effect on scroll */}
          {blogData.poster && (
            <div className="relative w-full h-[300px] sm:h-[480px] rounded-[32px] overflow-hidden mb-12 border border-neutral-250/50 dark:border-zinc-800/40 bg-neutral-100 dark:bg-zinc-900 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_30px_70px_-15px_rgba(0,0,0,0.6)] group/poster">
              <motion.div
                style={{ y: posterY, scale: posterScale, opacity: posterOpacity }}
                className="absolute inset-0 w-full h-full"
              >
                <Image
                  src={blogData.poster}
                  alt={`Cover banner for ${blogData.title}`}
                  fill
                  sizes="(max-width: 1024px) 100vw, 1024px"
                  className="object-cover"
                  priority
                />
              </motion.div>
              {/* Double gloss inner reflection border */}
              <div className="absolute inset-0 rounded-[32px] border border-white/10 pointer-events-none z-10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
            </div>
          )}

          {/* Content Pane - Wrapped inside a premium styled Reading Canvas */}
          <div className="relative border border-neutral-200/50 dark:border-zinc-800/30 bg-white/40 dark:bg-zinc-950/20 backdrop-blur-md rounded-[32px] p-6 sm:p-12 shadow-2xl transition-all duration-300 hover:shadow-indigo-500/5 dark:hover:shadow-indigo-950/5 mb-16">
            <div className="prose prose-md sm:prose-lg dark:prose-invert max-w-none font-outfit leading-[1.85] text-neutral-800 dark:text-zinc-200 tracking-normal focus:outline-none">
              <ReadOnlyEditor contentString={blogData.content} />
            </div>
          </div>

          {/* End of Article Footer Section */}
          <footer className="border-t border-neutral-200/50 dark:border-zinc-850/40 pt-10 pb-8 mt-12 space-y-8">
            <div className="p-6 sm:p-8 rounded-[32px] border border-neutral-200/50 dark:border-zinc-800/30 bg-white/40 dark:bg-zinc-950/20 backdrop-blur-md flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-center sm:text-left flex-col sm:flex-row">
                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-blue-500/10 via-indigo-500/10 to-purple-500/10 flex items-center justify-center text-lg font-bold text-indigo-600 dark:text-indigo-400 font-outfit border border-indigo-500/20 shadow-md">
                  {authorInitials}
                </div>
                <div>
                  <p className="text-xs font-bold text-neutral-400 dark:text-zinc-500 uppercase tracking-widest font-outfit">
                    Published By
                  </p>
                  <p className="text-base font-bold text-neutral-805 dark:text-zinc-250 font-recoleta mt-0.5">
                    {blogData.author || "Anonymous"}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-zinc-500 font-outfit mt-1">
                    Thank you for reading this publication in the devebsy journal.
                  </p>
                </div>
              </div>

                <Button
                  render={(props) => <Link {...(props as any)} href="/blog" />}
                  className="rounded-full font-bold font-outfit px-6 bg-neutral-900 hover:bg-neutral-800 dark:bg-white dark:hover:bg-neutral-100 text-white dark:text-black shadow-md shrink-0 hover:scale-102 active:scale-98 transition-all"
                  size="md"
                >
                  Explore Journal
                </Button>
            </div>

            {/* Social Share section */}
            <div className="text-center space-y-3">
              <p className="text-xs font-bold text-neutral-400 dark:text-zinc-500 uppercase tracking-widest font-outfit">
                Share this publication
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={shareToTwitter}
                  className="px-4 py-2.5 rounded-full border border-neutral-250/50 dark:border-zinc-800/80 hover:border-neutral-350 dark:hover:border-zinc-700 bg-white/50 dark:bg-zinc-900/30 hover:bg-neutral-100 dark:hover:bg-zinc-900 text-xs font-semibold text-neutral-700 dark:text-zinc-300 transition-all flex items-center gap-2 cursor-pointer shadow-sm"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Share to X
                </button>
                <button
                  onClick={shareToLinkedIn}
                  className="px-4 py-2.5 rounded-full border border-neutral-250/50 dark:border-zinc-800/80 hover:border-neutral-350 dark:hover:border-zinc-700 bg-white/50 dark:bg-zinc-900/30 hover:bg-neutral-100 dark:hover:bg-zinc-900 text-xs font-semibold text-neutral-700 dark:text-zinc-300 transition-all flex items-center gap-2 cursor-pointer shadow-sm"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                  </svg>
                  Share to LinkedIn
                </button>
              </div>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}
